import React, { useState, useRef, useEffect } from "react";
import { FaEdit, FaTrashAlt, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import { deleteAnnonce } from "./../../../services/annoncesServices";
import Button from "./../../../components/Button/Button";
import EditAnnonceModal from "./EditAnnonceModal";
import DOMPurify from "dompurify";

const FALLBACK = "/img/illustration_petite_annonce_qb825q.webp";

/**
 * Props ajoutées:
 * - currentUserId: id de l'utilisateur courant (pour vérifier la propriété)
 * - canModerate: bool (roles 1,6,7) pouvant modifier/supprimer toute annonce
 */
const AnnonceCard = ({ annonce, refreshList, currentUserId, canModerate }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Clamp "Lire plus"
  const contentRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [needsClamp, setNeedsClamp] = useState(false);
  const [maxHeightPx, setMaxHeightPx] = useState(0);

  // Re-mesure quand le contenu change
  useEffect(() => {
    const el = contentRef.current;
    if (!el) {
      setNeedsClamp(false);
      return;
    }
    const measure = () => {
      // calcule la hauteur max pour ~7 lignes selon la line-height calculée
      const cs = window.getComputedStyle(el);
      const lineH = parseFloat(cs.lineHeight || "20");
      const lines = 7;
      const maxH = lineH * lines;
      setMaxHeightPx(maxH);

      // mesure du contenu complet
      const prevMax = el.style.maxHeight;
      const prevOv = el.style.overflow;
      el.style.maxHeight = "none";
      el.style.overflow = "visible";
      const sh = el.scrollHeight;
      el.style.maxHeight = prevMax;
      el.style.overflow = prevOv;

      setNeedsClamp(sh > maxH + 2);
    };

    // mesure après layout
    const id = requestAnimationFrame(measure);
    const ro = new ResizeObserver(() => requestAnimationFrame(measure));
    ro.observe(el);

    return () => {
      cancelAnimationFrame(id);
      ro.disconnect();
    };
  }, [annonce?.content]);

  // Si on change d’annonce, on replie
  useEffect(() => {
    setExpanded(false);
  }, [annonce?.id]);

  const imageUrl = annonce.image_url || FALLBACK;
  const isActive = !!annonce.is_active;

  const isOwner =
    currentUserId && Number(currentUserId) === Number(annonce.user_id);
  const canModify = canModerate || isOwner; // édition/suppression

  const handleDeleteAnnonce = async () => {
    if (!canModify) return;
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await deleteAnnonce(annonce.id);
      await refreshList?.();
    } catch (error) {
      console.error("Erreur lors de la suppression de l'annonce:", error);
    }
  };

  const openEdit = () => setIsEditModalOpen(true);
  const closeEdit = async () => {
    setIsEditModalOpen(false);
    await refreshList?.();
  };

  return (
    <article className="rounded-2xl overflow-hidden bg-white/90 dark:bg-slate-800/80 ring-1 ring-slate-200/70 dark:ring-slate-700/70 hover:shadow transition">
      {/* Media */}
      <div className="relative h-48 w-full overflow-hidden">
        <img
          src={imageUrl}
          alt={annonce.title}
          onError={(e) => (e.currentTarget.src = FALLBACK)}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="px-2.5 py-1 rounded-full text-xs bg-black/70 text-white">
            {annonce.category}
          </span>
          {!isActive && (canModerate || isOwner) && (
            <span className="px-2.5 py-1 rounded-full text-xs bg-yellow-400 text-black">
              Non publiée
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col gap-3">
        <header>
          <h3 className="text-lg font-bold">{annonce.title}</h3>
          <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
            Publié le{" "}
            {new Date(annonce.date_annonce).toLocaleDateString("fr-FR", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
            {annonce?.user ? (
              <>
                {" · "}par {annonce.user.prenom} {annonce.user.nom}
                {annonce.club_user ? ` — ${annonce.club_user}` : ""}
              </>
            ) : null}
          </div>
        </header>

        <div className="relative">
          <div
            ref={contentRef}
            className="text-slate-700 dark:text-slate-200 prose prose-sm max-w-none [&_a]:inline [&_a]:text-indigo-600 hover:[&_a]:underline transition-[max-height] duration-200"
            style={{
              maxHeight:
                !expanded && needsClamp && maxHeightPx
                  ? `${maxHeightPx}px`
                  : "none",
              overflow: !expanded && needsClamp ? "hidden" : "visible",
            }}
            onClick={(e) => {
              // Laisse les vrais liens fonctionner, sinon bloque la propagation
              if (e.target && e.target.closest && e.target.closest("a")) return;
              e.stopPropagation();
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(annonce.content || ""),
            }}
          />
          {!expanded && needsClamp && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white dark:from-slate-800 to-transparent" />
          )}
        </div>

        {needsClamp && (
          <div className="mt-2">
            <Button
              text={expanded ? "Réduire" : "Lire plus"}
              variant="subtle"
              className="!rounded-xl"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((x) => !x);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Contacts */}
        {(annonce.tel_user || annonce.mail_user) && (
          <div className="flex flex-wrap gap-2 mt-1">
            {annonce.tel_user && (
              <Button
                text="Appeler"
                icon={FaPhoneAlt}
                className="!rounded-xl"
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `tel:${annonce.tel_user}`;
                }}
              />
            )}
            {annonce.mail_user && (
              <Button
                text="Envoyer un mail"
                icon={FaEnvelope}
                className="!rounded-xl"
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `mailto:${annonce.mail_user}`;
                }}
              />
            )}
          </div>
        )}

        {/* Actions (édition/suppression) */}
        {canModify && (
          <div className="flex justify-end gap-2 mt-2">
            <Button
              text="Modifier"
              onClick={openEdit}
              icon={FaEdit}
              variant="subtle"
              className="!rounded-xl"
            />
            <Button
              text="Supprimer"
              onClick={handleDeleteAnnonce}
              icon={FaTrashAlt}
              className="!rounded-xl !bg-red-600 hover:!bg-red-700 !text-white"
            />
          </div>
        )}
      </div>

      {/* Modal édition */}
      <EditAnnonceModal
        isOpen={isEditModalOpen}
        onClose={closeEdit}
        fetchAnnonces={refreshList}
        annonce={annonce}
      />
    </article>
  );
};

export default AnnonceCard;
